import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWhatsAppMessageTemplates } from "@/hooks/useWhatsAppMessageTemplates";
import { TemplatePreview } from "./TemplatePreview";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { getEffectiveVariables } from "@/utils/whatsapp-template-utils";
import { getWhatsAppErrorDescription } from "@/lib/whatsappErrors";

interface SendTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneNumber: string;
  contactId?: string;
  leadId?: string;
  contactName?: string;
  onTemplateSent?: () => void;
  /** When 'support', routes via the Atendimento WhatsApp number instead of the commercial sender. */
  channel?: 'support';
  /** Optional explicit E.164 sender override. */
  fromNumber?: string;
  /** Atendimento session id — links the inserted template message to the open ticket. */
  sessionId?: string;
}

export function SendTemplateModal({
  open,
  onOpenChange,
  phoneNumber,
  contactId,
  leadId,
  contactName,
  onTemplateSent,
  channel,
  fromNumber,
  sessionId,
}: SendTemplateModalProps) {
  const { templates } = useWhatsAppMessageTemplates();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [variableValues, setVariableValues] = useState<Record<number, string>>({});
  const [isSending, setIsSending] = useState(false);

  const approvedTemplates = templates?.filter(t => t.status === 'approved') || [];
  const selectedTemplate = approvedTemplates.find(t => t.id === selectedTemplateId);
  const effectiveVariables = selectedTemplate ? getEffectiveVariables(selectedTemplate) : [];
  console.log('Templates available:', templates?.length);
  console.log('Approved templates:', approvedTemplates.length);
  console.log('Selected template ID:', selectedTemplateId);
  console.log('Selected template:', selectedTemplate);

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Selecione um template');
      return;
    }

    // Validate template_id
    if (!selectedTemplate.template_id || selectedTemplate.template_id.trim() === '') {
      toast.error('Template inválido: ID ausente');
      console.error('Template missing ID:', selectedTemplate);
      return;
    }

    // Check if all required variables are filled
    const requiredVars = effectiveVariables;
    const missingVars = requiredVars.filter((_, index) => !variableValues[index + 1]);
    if (missingVars.length > 0) {
      toast.error('Preencha todas as variáveis do template');
      return;
    }

    // Validate and format phone number to E.164
    let formattedPhone = phoneNumber.trim();
    
    // Remove common formatting characters
    formattedPhone = formattedPhone.replace(/[\s\-\(\)\.]/g, '');
    
    // Ensure it starts with +
    if (!formattedPhone.startsWith('+')) {
      // If it doesn't start with +, assume US number and add +1
      if (formattedPhone.length === 10) {
        formattedPhone = `+1${formattedPhone}`;
      } else {
        formattedPhone = `+${formattedPhone}`;
      }
    }
    
    // Basic E.164 validation: must be 7-15 digits after the +
    const phoneDigits = formattedPhone.substring(1);
    if (!/^\d{7,15}$/.test(phoneDigits)) {
      toast.error('Formato de telefone inválido', {
        description: 'Phone must be in E.164 format (e.g., +15551234567 for US)',
        duration: 5000
      });
      return;
    }

    console.log('Sending template:', {
      template: selectedTemplate.name,
      template_id: selectedTemplate.template_id,
      originalPhone: phoneNumber,
      formattedPhone,
      variables: variableValues
    });

    setIsSending(true);
    try {
      // Monta array de valores das variáveis na ordem
      const variables = effectiveVariables;
      const values = variables.map((_, index) => variableValues[index + 1] || '');

      // Renderiza o corpo localmente para salvar exatamente o que foi enviado
      const applyVars = (text?: string) => {
        if (!text) return '';
        let t = text;
        values.forEach((value, index) => {
          const n = index + 1;
          const patterns = [
            new RegExp(`\\{\\{\\s*${n}\\s*\\}}`, 'g'),
            new RegExp(`\\{${n}\\}`, 'g'),
            new RegExp(`\\$\\{${n}\\}`, 'g'),
            new RegExp(`%${n}%`, 'g'),
          ];
          patterns.forEach((rgx) => (t = t.replace(rgx, value)));
        });
        return t;
      };

      let renderedBody = applyVars(selectedTemplate.body_text);
      const headerText = applyVars(selectedTemplate.header_text);
      const footerText = applyVars(selectedTemplate.footer_text);
      if (headerText) renderedBody = `*${headerText}*\n\n` + renderedBody;
      if (footerText) renderedBody = renderedBody + `\n\n_${footerText}_`;

      console.log('Invoking twilio-whatsapp edge function...');

      const { data, error } = await supabase.functions.invoke('twilio-whatsapp', {
        body: {
          action: 'sendTemplate',
          to: formattedPhone,
          template_sid: selectedTemplate.template_id,
          template_variables: values,
          contactId: contactId,
          leadId: leadId,
          rendered_body: renderedBody,
          channel,
          fromNumber,
          sessionId,
        }
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        let details = '';
        try {
          details = (await (error as any)?.context?.text?.()) || '';
        } catch { /* ignore */ }
        const code = details.match(/\b(21211|21610|21408|63016|63032|63024|63049|30003|30005|30006|30008)\b/)?.[1];
        if (code) throw new Error(getWhatsAppErrorDescription(code));
        throw new Error('Não foi possível enviar o template. Tente novamente.');
      }

      // Check response structure
      if (!data) {
        throw new Error('No response data from edge function');
      }

      if (data.success === false || data.error) {
        throw new Error(data.error || 'Unknown error from edge function');
      }

      toast.success('Template enviado com sucesso!');
      
      // Trigger message reload
      if (onTemplateSent) {
        onTemplateSent();
      }
      
      onOpenChange(false);
      setSelectedTemplateId('');
      setVariableValues({});
    } catch (error: any) {
      console.error('❌ Error sending template:', {
        error,
        message: error?.message,
        stack: error?.stack,
        template: selectedTemplate?.name
      });
      
      // Extract meaningful error message
      let errorMessage = 'Failed to send template';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast.error(errorMessage, {
        description: `Template: ${selectedTemplate.name}`,
        duration: 5000
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Enviar Template do WhatsApp
          </DialogTitle>
          {contactName && (
            <p className="text-sm text-muted-foreground">
              Para: {contactName} ({phoneNumber})
            </p>
          )}
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-4">
            <div>
              <Label>Selecione o Template</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha um template aprovado" />
                </SelectTrigger>
                <SelectContent>
                  {approvedTemplates.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      Nenhum template aprovado disponível
                    </div>
                  ) : (
                    approvedTemplates
                      .filter((template) => template.id && template.id.trim() !== '')
                      .map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && effectiveVariables.length > 0 && (
              <div className="space-y-3">
                <Label>Variáveis do Template</Label>
                {effectiveVariables.map((variable, index) => (
                  <div key={index}>
                    <Label className="text-xs text-muted-foreground">
                      {`{{${index + 1}}}`} - {variable}
                    </Label>
                    <Input
                      placeholder={selectedTemplate.example_values?.[index] || `Informe ${variable}`}
                      value={variableValues[index + 1] || ''}
                      onChange={(e) => setVariableValues({
                        ...variableValues,
                        [index + 1]: e.target.value
                      })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview Section */}
          <div>
            <Label>Pré-visualização</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Veja como a mensagem será exibida
            </p>
            {selectedTemplate ? (
              <TemplatePreview
                headerType={selectedTemplate.header_type}
                headerText={selectedTemplate.header_text}
                bodyText={selectedTemplate.body_text}
                footerText={selectedTemplate.footer_text}
                buttons={selectedTemplate.buttons}
                exampleValues={Object.values(variableValues)}
              />
            ) : (
              <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
                Selecione um template para visualizar
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={!selectedTemplate || isSending}
          >
            {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
